<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { formatDistanceToNow } from 'date-fns';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const { currentAccount } = useAccount();

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  // Handle Live Visitors route - go back to live visitors list
  if (name === 'live_visitors_conversation') {
    return {
      name: 'live_visitors_index',
      params: { accountId: accountId.value },
    };
  }

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const conversationId = computed(() => currentChat.value?.id);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const pageVisits = computed(() => {
  const visits =
    currentConversationMetaData.value?.additional_attributes?.page_visits || [];
  return visits
    .slice()
    .reverse()
    .slice(0, 10)
    .map(visit => {
      const date = new Date(visit.timestamp);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      const relativeTime = formatDistanceToNow(date, { addSuffix: true });
      return {
        ...visit,
        formattedDate,
        relativeTime,
      };
    });
});

const hasPageVisits = computed(() => pageVisits.value.length > 0);
const showPageVisits = ref(true);

// AI Lock functionality
const isAILocked = computed(() => {
  return currentChat.value?.additional_attributes?.ai_locked === true;
});
const showAILockButton = computed(() => {
  // Check if AI is enabled on the account
  // The property might be captain_ai_enabled or nested in features
  const aiEnabled =
    currentAccount.value?.captain_ai_enabled ||
    currentAccount.value?.features?.captain_ai_enabled;
  return aiEnabled === true;
});

const toggleAILock = async () => {
  try {
    await store.dispatch('toggleAILock', {
      conversationId: currentChat.value.id,
    });
    useAlert(
      isAILocked.value
        ? t('CONVERSATION.AI_UNLOCKED_SUCCESS')
        : t('CONVERSATION.AI_LOCKED_SUCCESS')
    );
  } catch (error) {
    useAlert(t('CONVERSATION.AI_LOCK_TOGGLE_ERROR'));
  }
};

const pageVisitHistoryWithCount = computed(() => {
  return t('CONTACT_PANEL.PAGE_VISIT_HISTORY');
});

const pageVisitCountText = computed(() => {
  return `(${pageVisits.value.length})`;
});

const isContactOnline = computed(() => {
  return currentContact.value?.availability_status === 'online';
});

const contactLastSeenAt = computed(() => {
  return currentChat.value?.contact_last_seen_at || currentChat.value?.contactLastSeenAt;
});

const lastSeenText = computed(() => {
  if (isContactOnline.value) {
    return t('CONVERSATION.HEADER.LIVE');
  }
  
  if (!contactLastSeenAt.value) {
    return '';
  }
  
  // Handle both Unix timestamp (seconds) and milliseconds
  const timestamp = typeof contactLastSeenAt.value === 'string' 
    ? new Date(contactLastSeenAt.value).getTime() / 1000 
    : contactLastSeenAt.value > 1000000000000 
      ? contactLastSeenAt.value / 1000 
      : contactLastSeenAt.value;
  
  if (!timestamp || timestamp === 0) {
    return '';
  }
  
  const relativeTime = formatDistanceToNow(new Date(timestamp * 1000), { addSuffix: true });
  return `${t('CONVERSATION.HEADER.LAST_SEEN')} ${relativeTime}`;
});
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col w-full border-b bg-n-background border-n-weak"
  >
    <div
      class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2"
    >
      <div
        class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
      >
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="ltr:mr-2 rtl:ml-2"
        />
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
            <span
              v-if="lastSeenText"
              :class="isContactOnline ? 'text-n-green-10 font-medium' : 'text-n-slate-11'"
            >
              {{ lastSeenText }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
      >
        <SLACardLabel
          v-if="hasSlaPolicyId"
          :chat="chat"
          show-extended-info
          :parent-width="width"
          class="hidden md:flex"
        />
        <ButtonV4
          v-if="showAILockButton"
          v-tooltip="
            isAILocked
              ? $t('CONVERSATION.UNLOCK_AI_TOOLTIP')
              : $t('CONVERSATION.LOCK_AI_TOOLTIP')
          "
          size="sm"
          variant="ghost"
          :color="isAILocked ? 'amber' : 'slate'"
          :icon="isAILocked ? 'i-lucide-lock' : 'i-lucide-lock-open'"
          class="rounded-md"
          @click="toggleAILock"
        />
        <MoreActions :conversation-id="currentChat.id" />
      </div>
    </div>

    <!-- Page Visit History Roadmap -->
    <div
      v-if="hasPageVisits && showPageVisits"
      class="px-3 py-2 border-t border-n-slate-3 bg-n-slate-1"
    >
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <fluent-icon icon="navigation" size="14" class="text-n-slate-9" />
          <span class="text-xs font-semibold text-n-slate-11">
            {{ $t('CONTACT_PANEL.PAGE_VISIT_HISTORY') }}
          </span>
        </div>
        <button
          class="text-n-slate-9 hover:text-n-slate-11"
          @click="showPageVisits = false"
        >
          <fluent-icon icon="dismiss" size="14" />
        </button>
      </div>
      <div class="space-y-1.5 max-h-48 overflow-y-auto overflow-x-hidden">
        <div
          v-for="(visit, index) in pageVisits"
          :key="index"
          class="flex items-start gap-2 py-1 hover:bg-n-slate-2 rounded px-1 -mx-1 transition-colors"
        >
          <div class="flex-shrink-0 mt-0.5">
            <fluent-icon icon="arrow-right" size="12" class="text-n-slate-7" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <a
                :href="visit.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs font-medium text-n-brand hover:underline"
                :title="visit.title || visit.url"
              >
                {{ visit.title || visit.url }}
              </a>
              <span class="text-[10px] text-n-slate-9">
                {{ visit.formattedDate }}
                <span class="mx-1" aria-hidden="true">{{
                  $t('COMMON.BULLET_SEPARATOR')
                }}</span>
                {{ visit.relativeTime }}
              </span>
            </div>
            <div
              class="text-[10px] text-n-slate-9 truncate mt-0.5"
              :title="visit.url"
            >
              {{ visit.url }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="hasPageVisits && !showPageVisits"
      class="px-3 py-1.5 border-t border-n-slate-3 bg-n-slate-1"
    >
      <button
        class="flex items-center gap-2 text-xs text-n-slate-9 hover:text-n-slate-11"
        @click="showPageVisits = true"
      >
        <fluent-icon icon="navigation" size="12" />
        <span>
          {{ pageVisitHistoryWithCount }}
          <span class="mx-1">{{ pageVisitCountText }}</span>
        </span>
        <fluent-icon icon="chevron-down" size="12" />
      </button>
    </div>
  </div>
</template>
